<script lang="ts">
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Modal from './components/Modal.vue'
import MegaMenu from './components/MegaMenu.vue'
import { DisplayMode, useDisplayConfigStore } from './stores/displayConfig'
import { useModalControlStore } from './stores/modalControl'
import { useSnackbarControlStore } from './stores/snackbarControl'
import { useLoggedInUserStore, LIURole } from './stores/loggedInUser'
import { ref, type StyleValue } from 'vue'
import { storeToRefs } from 'pinia'
import router from './router'
import BookstackSearch from './components/BookstackSearch.vue'
import { pathJoin } from './utils/tsutils'

interface Button {
  icon: string
  text: string
  to?: string
  roles?: LIURole[]
  noKiosk?: boolean
  pop?: Button[]
  action?: () => void
  external?: boolean
  badgeContent?: number
  badgeName?: string
  hideMobile?: boolean
  overrideFunction?: () => void
  auxiliaryValidation?: () => boolean
}

function logout() {
  const user = useLoggedInUserStore()
  user.logout()
}

export default {
  name: 'App',
  components: {
    RouterLink,
    RouterView,
    FontAwesomeIcon,
    Modal,
    MegaMenu,
    BookstackSearch
  },
  data: () => ({
    buttons: [
      {
        icon: 'fa-house',
        text: 'Home',
        to: '/',
        hideMobile: true
      },
      {
        icon: 'fa-chalkboard-user',
        text: 'Events',
        pop: [
          {
            icon: 'fa-calendar-days',
            text: 'All Upcoming Events',
            to: '/signup/upcomingworkshops'
          },
          {
            icon: 'fa-notes-medical',
            text: 'Process Event',
            to: '/scheduling/process',
            roles: [LIURole.EventProcessor]
          },
          { icon: 'fa-user-tie', text: 'My Events', to: '/signup/myevents' }
        ]
      },
      {
        icon: '$nounCalEq',
        text: 'Scheduling',
        noKiosk: true,
        roles: [
          LIURole.EventAdmin,
          LIURole.EventScheduler,
          LIURole.EventProcessor,
          LIURole.CalendarAdmin
        ],
        pop: [
          {
            icon: 'fa-users',
            text: 'Event Categories',
            to: '/scheduling/managecategories',
            roles: [LIURole.EventAdmin]
          },
          {
            icon: 'fa-user-lock',
            text: 'Events',
            to: '/scheduling/manageevents',
            roles: [LIURole.EventAdmin]
          },
          {
            icon: 'fa-map-marker-alt',
            text: 'Schedule Instance',
            to: '/scheduling/scheduleinstance',
            roles: [LIURole.EventAdmin, LIURole.EventScheduler]
          },
          {
            icon: 'fa-cogs',
            text: 'Manage Event Instances',
            to: '/scheduling/manageinstances',
            roles: [LIURole.EventAdmin, LIURole.EventScheduler]
          },
          {
            icon: '$nounCalDays',
            text: 'Calendar Manager',
            to: '/calendarmanager',
            roles: [LIURole.CalendarAdmin]
          },
          {
            icon: '$nounEvents',
            text: 'View Calendars',
            to: '/calendars',
            roles: [LIURole.EventScheduler, LIURole.CalendarAdmin, LIURole.EventAdmin]
          },
          {
            icon: 'fa-check',
            text: 'Event Records',
            to: '/scheduling/records',
            roles: [LIURole.EventAdmin, LIURole.EventProcessor, LIURole.EventScheduler]
          }
        ],
        hideMobile: true
      },
      {
        icon: '$nounBell',
        text: 'Requests',
        noKiosk: true,
        pop: [
          { icon: '$nounBell', text: 'Make a Request', to: '/requests/navigate' },
          {
            icon: 'fa-registered',
            text: 'Manage Requests',
            to: '/requests/manage',
            roles: [LIURole.RequestsAdmin, LIURole.Administrator]
          },
          {
            icon: 'fa-check',
            text: 'View Submission',
            to: '/requests/viewsubmissions',
            roles: [LIURole.RequestsAdmin, LIURole.RequestsProcessor]
          },
          {
            icon: 'fa-user-tie',
            text: 'My Requests',
            to: '/requests/myrequests'
          }
        ]
      },
      {
        icon: '$nounStore',
        text: 'Payments',
        noKiosk: true,
        pop: [
          { icon: '$nounStore', text: 'Make a Payment', to: '/payment/store' },
          {
            icon: 'fa-dollar-sign',
            text: 'Transaction Viewer',
            to: '/transactionviewer',
            roles: [LIURole.TransactionReader]
          }
        ]
      },
      {
        icon: 'fa-gear',
        text: 'Settings',
        noKiosk: true,
        roles: [
          LIURole.Administrator,
          LIURole.CalendarAdmin,
          LIURole.ContentAdmin,
          LIURole.StoreAdmin,
          LIURole.ClassScheduler,
          LIURole.EventAdmin,
          LIURole.RequestsAdmin
        ],
        pop: [
          {
            icon: 'fa-users',
            text: 'Manage Groups',
            to: '/admin/groups',
            roles: [LIURole.Administrator]
          },
          {
            icon: 'fa-user-lock',
            text: 'Manage Permissions',
            to: '/admin/permissions',
            roles: [LIURole.Administrator]
          },
          {
            icon: 'fa-map-marker-alt',
            text: 'Manage Locations',
            to: '/admin/locations',
            roles: [LIURole.CalendarAdmin]
          },
          {
            icon: 'fa-cogs',
            text: 'Manage Site Settings',
            to: '/admin/systemSettings',
            roles: [LIURole.Administrator]
          },
          {
            icon: 'fa-clipboard-list',
            text: 'View Event Log',
            to: '/admin/eventLog',
            roles: [LIURole.Administrator]
          },
          {
            icon: 'fa-sitemap',
            text: 'Manage Registered Sites',
            to: '/admin/registeredSites',
            roles: [LIURole.Administrator]
          },
          {
            icon: 'fa-arrows-to-circle',
            text: 'Content Manager',
            to: '/content/manage',
            roles: [LIURole.ContentAdmin, LIURole.Administrator]
          },
          {
            icon: 'fa-rocket',
            text: 'Launch Hangfire Dashboard',
            to: '/api/hangfire',
            external: true,
            roles: [LIURole.Administrator]
          },
          {
            icon: 'fa-people-roof',
            text: 'Class Manager',
            to: '/admin/classes',
            roles: [LIURole.Administrator, LIURole.ClassScheduler, LIURole.RequestsAdmin]
          },
          {
            icon: 'fa-home',
            text: 'Store Manager',
            to: '/admin/manageStore',
            roles: [LIURole.StoreAdmin]
          },
          {
            icon: 'fa-piggy-bank',
            text: 'Balance Manager',
            to: '/admin/balanceManager',
            roles: [LIURole.StoreAdmin]
          },
          {
            icon: 'fa-triangle-exclamation',
            text: 'Prereq Manager',
            to: '/admin/prereqManager',
            roles: [LIURole.EventAdmin]
          },
          {
            icon: 'fa-dollar-sign',
            text: 'Discount Group Manager',
            to: '/admin/discount',
            roles: [LIURole.RequestsAdmin]
          },
          {
            icon: 'fa-bug',
            text: 'Debug',
            to: '/admin/debug',
            roles: [LIURole.Administrator]
          },
          {
            icon: '$nounKiosk',
            text: 'Center Manager',
            to: '/centers/manage',
            roles: [LIURole.Administrator]
          }
        ]
      },
      {
        icon: 'fa-screwdriver-wrench',
        text: 'Tools',
        noKiosk: true,
        hideMobile: true,
        roles: [
          LIURole.Administrator,
          LIURole.EventAdmin,
          LIURole.ContentAdmin,
          LIURole.EventProcessor
        ],
        pop: [
          {
            icon: 'fa-warehouse',
            text: 'Inventory System',
            to: 'https://itlp-inventory.int.colorado.edu/login',
            external: true,
            roles: [LIURole.Administrator, LIURole.EventAdmin, LIURole.ContentAdmin]
          },
          {
            icon: 'fa-link',
            text: 'Links Manager',
            to: '/tools/linksmanager',
            roles: [LIURole.Administrator, LIURole.EventAdmin, LIURole.ContentAdmin],
            auxiliaryValidation: () => useDisplayConfigStore().showLinksManager
          },
          {
            icon: 'fa-book',
            text: 'Internal Documentation',
            to: 'https://itlp.link/portal-docs',
            external: true,
            roles: [LIURole.EventProcessor]
          }
        ]
      },
      {
        icon: 'fa-user-tie',
        text: 'Profile',
        badgeContent: 0,
        badgeName: 'unreadMessages',
        pop: [
          {
            icon: 'fa-arrow-right-to-bracket',
            text: 'Login',
            to: '/login',
            auxiliaryValidation: () => {
              const user = useLoggedInUserStore()
              return !user.isLoggedIn
            }
          },
          {
            icon: 'fa-arrow-right-from-bracket',
            text: 'Logout',
            to: '/',
            action: () => logout(),
            auxiliaryValidation: () => {
              const user = useLoggedInUserStore()
              return user.isLoggedIn
            }
          },
          {
            icon: 'fa-message',
            text: 'Communication Settings',
            to: '/myAccount/communicationSettings',
            auxiliaryValidation: () => {
              const user = useLoggedInUserStore()
              return user.isLoggedIn
            }
          },
          {
            icon: 'fa-inbox',
            text: 'Messages',
            to: '/myAccount/MyMessages',
            auxiliaryValidation: () => {
              const user = useLoggedInUserStore()
              return user.isLoggedIn
            },
            badgeContent: 0,
            badgeName: 'unreadMessages'
          }
        ]
      }
    ] as Button[],
    navSelected: '',
    search: '',
    sideNavHeight: 0,
    DisplayMode,
    sideBarWidth: 145,
    headerRefreshKey: 0
  }),
  methods: {
    clickNavButton(button: Button) {
      this.navSelected = button.text
      if (button.overrideFunction) {
        button.overrideFunction()
        return
      }
      if (button.to !== undefined) {
        this.$router.push(button.to!)
      } else {
      }
    },
    logout() {
      this.user.logout()
    },
    clickInnerNavButton(pop: any) {
      if (pop.overrideFunction) {
        pop.overrideFunction()
        return
      }
      if (pop.action) {
        pop.action()
      }
      if (pop.external) window.open(pop.to, '_blank')
      else this.$router.push(pop.to)
    },
    getButtonStyling(text: string) {
      return {
        backgroundColor: this.navSelected === text ? '#dac899' : 'transparent'
      }
    },
    handleScroll() {
      if (!this.display.headerLocked && window.scrollY > 65) {
        this.display.headerLocked = true
        this.display.refresh()
      } else if (this.display.headerLocked && window.scrollY <= 65) {
        this.display.headerLocked = false
        this.display.refresh()
      }
    },
    pathJoin
  },
  computed: {
    routeName() {
      return useRoute().name
    },
    isMobile() {
      return this.display.displayMode === DisplayMode.Mobile
    },
    appBarStyle(): StyleValue {
      var position = 'fixed'
      if (!this.display.headerLocked) {
        position = 'absolute'
      }

      var topPadding = 10

      var left = 'none'
      if (this.display.displayMode === DisplayMode.Mobile) {
        left = '0'
      }

      var marginLeft = this.isMobile ? 0 : this.sideBarWidth

      var height = '115px'
      if (this.display.headerLocked) {
        height = '60px'
      }

      var alignItems = 'baseline'
      if (!this.display.showFullLogo) {
        alignItems = 'center'
      }

      return {
        display: 'flex',
        position: position,
        top: '0',
        left: left,
        zIndex: '500',
        backgroundColor: 'black',
        paddingTop: topPadding + 'px',
        marginLeft: marginLeft + 'px',
        width: 'calc(100% - ' + marginLeft + 'px)',
        height: height,
        alignItems: alignItems
      } as StyleValue
    },
    contentStyle() {
      var topPadding = 145
      if (!this.display.showFullLogo) {
        //topPadding -= 55 // the difference between the locked height and the normal height
      }
      if (this.display.displayMode === DisplayMode.Mobile) {
        topPadding -= 20 // mobile needs much less padding
      }

      var leftPadding = 25
      if (this.display.displayMode !== DisplayMode.Mobile) {
        leftPadding += this.sideBarWidth
      }

      if (!this.display.showAnyNav) {
        leftPadding = 25
        topPadding = 10
      }

      if (this.display.bannerMessage) {
        topPadding += 20
      }

      if (this.display.welcomeMessage) {
        topPadding += 20
      }

      var bottomPadding = 50
      if (this.display.displayMode === DisplayMode.Mobile) {
        bottomPadding = 100
      }

      return {
        paddingTop: topPadding + 'px',
        paddingLeft: leftPadding + 'px',
        paddingBottom: bottomPadding + 'px'
      }
    },
    getMobileMenuStyle() {
      return {
        //backgroundColor: 'rgba(0, 0, 0, 0.35)',
        zIndex: 1000,
        overflow: 'hidden',
        position: 'fixed'
      } as StyleValue
    },
    getDesktopMenuStyle() {
      return {
        //backgroundColor: 'rgba(0, 0, 0, 0.35)',
        zIndex: 1000,
        overflow: 'hidden',
        position: 'fixed'
        //top: '50%'
      } as StyleValue
    },
    getMegaMenuStyle() {
      return {
        //backgroundColor: 'rgba(0, 0, 0, 0.35)',
        zIndex: 1500,
        overflow: 'hidden',
        position: 'fixed'
        //top: '50%'
      } as StyleValue
    },
    sideNavStyle() {
      return {
        top: this.sideNavHeight + 'px',
        width: this.sideBarWidth + 'px'
      }
    }
  },
  async mounted() {
    // this.display.determineDisplayMode()
    // window.addEventListener('resize', this.display.determineDisplayMode)
    //window.addEventListener('click', this.display.potentiallyCloseNav)
    await this.user.initialize()

    // for making the header sticky
    window.addEventListener('scroll', this.handleScroll)

    this.user.expireTimer(router)
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  watch: {
    unreadMessages() {
      this.$data.buttons.filter((button) => {
        if (button.badgeName !== undefined && button.badgeName === 'unreadMessages') {
          button.badgeContent = this.user.unreadMessages
        }
        button.pop?.filter((pop) => {
          if (pop.badgeName !== undefined && pop.badgeName === 'unreadMessages') {
            pop.badgeContent = this.user.unreadMessages
          }
        })
      })
    }
  },
  created() {},
  setup() {
    const modalStore = useModalControlStore()
    const snackbarStore = useSnackbarControlStore()
    const display = useDisplayConfigStore()
    const user = useLoggedInUserStore()
    const { isLoggedIn, unreadMessages } = storeToRefs(user)

    display.refresh()

    const topBar = ref<HTMLElement | null>(null)

    return { modalStore, snackbarStore, user, isLoggedIn, unreadMessages, display, topBar }
  }
}
</script>

<template>
  <v-layout id="app">
    <Modal v-if="modalStore.showModalOuter" @close="modalStore.showModalOuter = false">
      <template v-slot:header>
        <h3>Custom header</h3>
      </template>
    </Modal>
    <v-snackbar
      v-if="snackbarStore.currentSnackbar"
      :model-value="true"
      :color="snackbarStore.currentSnackbar.color || 'info'"
      :timeout="snackbarStore.currentSnackbar.timeout ?? 3000"
      @update:model-value="snackbarStore.closeSnackbar"
    >
      {{ snackbarStore.currentSnackbar.message }}
    </v-snackbar>
    <div id="wrapper">
      <div
        id="header"
        v-if="display.showAnyNav"
        :style="appBarStyle"
        :key="headerRefreshKey"
        role="banner"
      >
        <div id="full-logo" v-if="display.showFullLogo">
          <img
            v-if="display.lockupURL"
            @click="$router.push('/')"
            :src="pathJoin(['/api/Content/', display.lockupURL])"
            alt="CU Banner"
          />
        </div>
        <div
          id="smaller-logo"
          class="logo"
          v-if="!display.showFullLogo"
          style="margin-left: 30px; margin-right: 30px"
        >
          <img
            v-if="display.squareLogoURL"
            @click="$router.push('/')"
            :src="pathJoin(['/api/Content/', display.squareLogoURL])"
            alt="Conductor logo"
            style="margin: 5px"
          />
        </div>
        <div class="w-100 d-flex justify-end" style="margin-bottom: 10px">
          <BookstackSearch v-if="display.showBookstackSearch && !display.isKiosk" />
        </div>
        <div
          v-if="!display.isKiosk"
          class="logo mr-4"
          style="display: flex; align-items: center; margin-bottom: 10px"
        >
          <v-icon
            id="mega-menu-toggle"
            icon="fa-bars"
            color="white"
            aria-label="Toggle Mega Menu"
          />
          <v-menu
            v-model="display.showingMegaMenu"
            class="mega-menu"
            activator="#mega-menu-toggle"
            v-bind:style="getMegaMenuStyle"
            location="start bottom"
            :close-on-content-click="false"
          >
            <MegaMenu />
          </v-menu>
        </div>
        <div
          v-else
          class="logo mr-4"
          style="display: flex; align-items: center; margin-bottom: 10px"
        >
          <v-icon
            v-if="!isLoggedIn"
            id="kiosk-login-button"
            icon="fa-sign-in"
            color="white"
            aria-label="Log In"
            @click="$router.push('/login')"
          />
          <v-icon
            v-else
            id="kiosk-login-button"
            icon="fa-sign-out"
            color="white"
            aria-label="Log Out"
            @click="
              () => {
                logout()
                $router.push('/')
              }
            "
          />
        </div>
        <div class="banner blue-banner" v-if="display.welcomeMessage">
          <Vue3Marquee :delay="2" :animateOnOverflowOnly="true" :pauseOnHover="true">
            <div id="banner-inner" v-html="display.welcomeMessage"></div>
          </Vue3Marquee>
        </div>
        <div class="banner red-banner" v-if="display.bannerMessage">
          <Vue3Marquee :delay="2" :animateOnOverflowOnly="true" :pauseOnHover="true">
            <div id="banner-inner">{{ display.bannerMessage }}</div>
          </Vue3Marquee>
        </div>
      </div>
      <div id="content" v-bind:style="contentStyle" role="main">
        <RouterView v-bind:key="$route.path" />
        <!-- Note: bind to path and not fullPath so that pages that wish to manage their own urls
             (i.e. tables with filters that should stay through "back" nav) can do so and manage
             exactly which parts get refreshed when the full path changes -->
      </div>
    </div>
  </v-layout>
  <div
    id="sideNav"
    v-if="display.displayMode != DisplayMode.Mobile && display.showAnyNav"
    v-bind:style="sideNavStyle"
    role="navigation"
  >
    <button
      v-for="(button, index) in buttons.filter(
        (button) =>
          (button.roles === undefined || user.hasOneOf(button.roles)) &&
          (button.noKiosk === undefined || !display.isKiosk)
      )"
      :key="index"
      @click="clickNavButton(button)"
      v-bind:style="getButtonStyling(button.text)"
    >
      <div v-if="isLoggedIn && button.badgeContent != undefined && button.badgeContent > 0">
        <v-badge class="ma-0 pa-0" style="position: relative" :content="button.badgeContent">
          <v-icon :icon="button.icon" />
          <span>{{ button.text }}</span>
        </v-badge>
      </div>
      <div v-else>
        <v-icon :icon="button.icon" />
        <span> {{ button.text }}</span>
      </div>
      <v-menu
        class="home-nav"
        activator="parent"
        v-if="button.pop"
        v-bind:style="getDesktopMenuStyle"
        location="end center"
      >
        <v-icon
          size="45"
          icon="fa-caret-left"
          color="#A2A4A3"
          style="position: absolute; top: calc(50% - 20px); left: -10px"
        />
        <v-card
          min-width="300"
          width="30%"
          rounded="shaped"
          style="left: 15px"
          elevation="0"
          color="#A2A4A3"
        >
          <v-card-title>
            <v-icon :icon="button.icon" />
            <span class="navMenuTitle">{{ button.text }}</span>
            <v-icon style="float: right; padding: 5px; cursor: pointer" size="md" icon="fa-x" />
          </v-card-title>
          <v-card-text
            ><v-list class="drawer overflow-y-hidden">
              <template
                v-for="(pop, index) in button.pop
                  .filter((pop) => pop.roles === undefined || user.hasOneOf(pop.roles))
                  .filter(
                    (pop) => pop.auxiliaryValidation === undefined || pop.auxiliaryValidation()
                  )"
                :key="index"
              >
                <v-list-item
                  v-if="pop.auxiliaryValidation === undefined || pop.auxiliaryValidation()"
                  @click="clickInnerNavButton(pop)"
                  style="width: 100vw"
                >
                  <template v-slot:prepend>
                    <v-badge
                      v-if="isLoggedIn && pop.badgeContent != undefined && pop.badgeContent > 0"
                      style="position: relative"
                      :content="button.badgeContent"
                    >
                      <v-icon :icon="pop.icon"></v-icon>
                    </v-badge>
                    <v-icon v-else :icon="pop.icon"></v-icon>
                  </template>
                  <v-list-item-title v-text="pop.text"></v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-card-text>
        </v-card>
      </v-menu>
    </button>
  </div>
  <div id="bottomNav" v-else-if="display.showAnyNav" role="navigation">
    <button
      v-for="(button, index) in buttons.filter(
        (button) =>
          (button.roles === undefined || user.hasOneOf(button.roles)) && !button.hideMobile
      )"
      :key="index"
      @click="clickNavButton(button)"
      v-bind:style="getButtonStyling(button.text)"
    >
      <div v-if="isLoggedIn && button.badgeContent != undefined && button.badgeContent > 0">
        <v-badge class="ma-0 pa-0" style="position: relative" :content="button.badgeContent">
          <v-icon :icon="button.icon" />
          <span>{{ button.text }}</span>
        </v-badge>
      </div>
      <div v-else>
        <v-icon :icon="button.icon" />
        <span> {{ button.text }}</span>
      </div>
      <v-menu
        activator="parent"
        v-if="button.pop"
        v-bind:style="getMobileMenuStyle"
        scroll-strategy="close"
      >
        <v-card rounded="shaped" style="" elevation="0" color="#A2A4A3">
          <v-card-title>
            <v-icon :icon="button.icon" />
            {{ button.text }}
            <v-icon style="float: right; padding: 5px; cursor: pointer" size="md" icon="fa-x" />
          </v-card-title>
          <v-card-text
            ><v-list class="drawer">
              <v-list-item
                v-for="(pop, index) in button.pop.filter(
                  (pop) =>
                    (pop.roles === undefined || user.hasOneOf(pop.roles)) &&
                    (pop.auxiliaryValidation === undefined || pop.auxiliaryValidation())
                )"
                :key="index"
                @click="clickInnerNavButton(pop)"
                style="width: 100vw"
              >
                <template v-slot:prepend>
                  <v-badge
                    v-if="isLoggedIn && pop.badgeContent != undefined && pop.badgeContent > 0"
                    style="position: relative"
                    :content="button.badgeContent"
                  >
                    <v-icon :icon="pop.icon"></v-icon>
                  </v-badge>
                  <v-icon v-else :icon="pop.icon"></v-icon>
                </template>
                <v-list-item-title v-text="pop.text"></v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-menu>
    </button>
  </div>
</template>

<style>
html,
body {
  height: 100%;
  margin: 0;
}

@media (min-width: 600px) {
  html,
  body {
    width: 100%;
  }
}

#app {
  height: inherit;
  width: inherit;
  flex-direction: column;
}

#wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

#content {
  width: 100%;
  max-width: 100vw;
  padding: 10px 25px 10px 0;
}

#full-logo {
  display: flex;
  padding: 10px;
  height: 90px;
  margin-bottom: 15px;
  width: auto;
  cursor: pointer;
}

.banner {
  position: absolute;
  bottom: -25px;
  height: 25px;
  width: 100%;
  color: white;
  font-weight: bold;
}

.blue-banner {
  background-color: rgb(2, 119, 189);
}

.red-banner {
  background-color: var(--color-cu-alert);
  bottom: -50px;
}

.banner a {
  color: white;
  text-decoration: underline;
}

#banner-inner {
  padding: 0 15px;
  white-space: nowrap;
  width: 100%;
}

#welcome-inner {
  padding: 0 15px;
  white-space: nowrap;
  width: 100%;
}

.navMenuTitle {
  font-size: 22px;
  font-weight: 700;
  margin-left: 15px;
}

#bottomNav {
  position: fixed;
  bottom: 0;
  width: 100vw;
  display: flex;
  justify-content: space-around;
  background-color: #cfb87c;
  z-index: 1000;
}

#bottomNav button {
  background-color: transparent;
  border: none;
  width: 100%;
  cursor: pointer;
  padding: 10px 0 5px 0;
}

#bottomNav button div {
  display: flex;
  flex-direction: column;
  align-items: center;
}

#sideNav {
  position: fixed;
  left: 0;
  width: 75px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #cfb87c;
  z-index: 1000;
}

#sideNav button div {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0;
}

.logo {
  cursor: pointer;
}

.logo img {
  height: 50px;
  width: 50px;
}

.logo i {
  font-size: 40px;
  margin: 0 15px 0 10px;
}

.home-nav > .v-overlay__content {
  width: 550px;
  left: 15px;
}

#kiosk-login-button {
  font-size: 60px;
}
</style>
